/**
 * IdleTimeout.jsx
 * ==========================================================================
 * Auto-logout after 30 minutes of inactivity, with 60-second warning dialog.
 *
 * How it works:
 *   1. Attach passive event listeners (mousemove, keydown, click, scroll, touchstart).
 *   2. Any activity resets the 29-minute (WARN) timer.
 *   3. At WARN (29 min), open modal with 60-sec countdown.
 *   4. Click "Tetap Login" → reset. Click "Logout" or countdown to zero → logout().
 *
 * Only mounted when user is authenticated (see App.js).
 * 2026-07-15
 */
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';

const IDLE_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes
const WARNING_LEAD_MS = 60 * 1000;      // show warning 60s before logout
const WARN_AT_MS = IDLE_TIMEOUT_MS - WARNING_LEAD_MS; // 29 min

// Events we consider "activity"
const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'click'];

export default function IdleTimeout() {
  const { user, logout } = useAuth();
  const [showWarn, setShowWarn] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(60);

  const warnTimerRef = useRef(null);
  const logoutTimerRef = useRef(null);
  const countdownIntervalRef = useRef(null);
  // Suppress activity-based resets while the warning dialog is open. Otherwise
  // a stray mousemove would keep resetting the timer even when the user
  // stopped paying attention.
  const inWarnRef = useRef(false);

  const clearTimers = useCallback(() => {
    if (warnTimerRef.current) { clearTimeout(warnTimerRef.current); warnTimerRef.current = null; }
    if (logoutTimerRef.current) { clearTimeout(logoutTimerRef.current); logoutTimerRef.current = null; }
    if (countdownIntervalRef.current) { clearInterval(countdownIntervalRef.current); countdownIntervalRef.current = null; }
  }, []);

  const doLogout = useCallback(() => {
    clearTimers();
    setShowWarn(false);
    inWarnRef.current = false;
    try { logout(); } catch (_) { /* noop */ }
    // Give router a beat to catch the state change; if still on protected route,
    // hard-redirect to /login as safety net.
    setTimeout(() => {
      try {
        const path = window.location.pathname || '';
        // 2026-02 FIX: Customer portal routes (/p/{slug}/...) - redirect to portal login
        const portalMatch = path.match(/^\/p\/([^\/]+)/);
        if (portalMatch) {
          const slug = portalMatch[1];
          if (path !== `/p/${slug}/login`) {
            window.location.assign(`/p/${slug}/login?reason=idle`);
          }
          return;
        }
        if (!path.startsWith('/login') && !path.startsWith('/portal') && !path.startsWith('/register')) {
          window.location.assign('/login?reason=idle');
        }
      } catch (_) { /* noop */ }
    }, 150);
  }, [logout, clearTimers]);

  const startCountdown = useCallback(() => {
    setSecondsLeft(60);
    setShowWarn(true);
    inWarnRef.current = true;
    countdownIntervalRef.current = setInterval(() => {
      setSecondsLeft((s) => (s > 0 ? s - 1 : 0));
    }, 1000);
    // Failsafe: force logout after WARNING_LEAD_MS regardless of countdown state
    logoutTimerRef.current = setTimeout(() => doLogout(), WARNING_LEAD_MS);
  }, [doLogout]);

  const resetTimers = useCallback(() => {
    if (inWarnRef.current) return; // don't reset while warning open
    clearTimers();
    warnTimerRef.current = setTimeout(() => startCountdown(), WARN_AT_MS);
  }, [clearTimers, startCountdown]);

  const handleStayLoggedIn = useCallback(() => {
    setShowWarn(false);
    inWarnRef.current = false;
    clearTimers();
    warnTimerRef.current = setTimeout(() => startCountdown(), WARN_AT_MS);
  }, [clearTimers, startCountdown]);

  // Attach listeners only when authenticated.
  useEffect(() => {
    if (!user) {
      clearTimers();
      setShowWarn(false);
      inWarnRef.current = false;
      return;
    }
    resetTimers();
    const opts = { passive: true, capture: true };
    ACTIVITY_EVENTS.forEach((ev) => window.addEventListener(ev, resetTimers, opts));
    // Also reset if tab becomes visible (user may have switched away then back)
    const onVisibility = () => {
      if (document.visibilityState === 'visible') resetTimers();
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      ACTIVITY_EVENTS.forEach((ev) => window.removeEventListener(ev, resetTimers, opts));
      document.removeEventListener('visibilitychange', onVisibility);
      clearTimers();
    };
  }, [user, resetTimers, clearTimers]);

  // Listen for token-expired event dispatched by axios 401 interceptor.
  useEffect(() => {
    const onAuthExpired = () => doLogout();
    window.addEventListener('auth:expired', onAuthExpired);
    return () => window.removeEventListener('auth:expired', onAuthExpired);
  }, [doLogout]);

  if (!user) return null;

  return (
    <Dialog open={showWarn} onOpenChange={(open) => { if (!open) handleStayLoggedIn(); }}>
      <DialogContent className="sm:max-w-md" data-testid="idle-timeout-dialog">
        <DialogHeader>
          <DialogTitle className="text-amber-600 dark:text-amber-400">
            Sesi Anda Akan Berakhir
          </DialogTitle>
          <DialogDescription className="pt-2">
            Anda tidak aktif selama 29 menit. Session akan otomatis logout dalam{' '}
            <span className="font-bold text-red-600 dark:text-red-400" data-testid="idle-countdown">
              {secondsLeft}
            </span>{' '}
            detik jika tidak ada aktivitas.
          </DialogDescription>
        </DialogHeader>
        <div className="flex items-center justify-center py-2">
          <div className="relative w-32 h-32">
            <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100">
              <circle cx="50" cy="50" r="45" fill="none" stroke="currentColor" strokeWidth="8" className="text-slate-200 dark:text-slate-700" />
              <circle
                cx="50" cy="50" r="45" fill="none" stroke="currentColor" strokeWidth="8"
                strokeLinecap="round"
                className="text-amber-500"
                strokeDasharray={`${(secondsLeft / 60) * 283} 283`}
                style={{ transition: 'stroke-dasharray 1s linear' }}
              />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center text-3xl font-bold">
              {secondsLeft}s
            </div>
          </div>
        </div>
        <DialogFooter className="gap-2 sm:gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={doLogout}
            data-testid="idle-logout-now"
          >
            Logout Sekarang
          </Button>
          <Button
            type="button"
            onClick={handleStayLoggedIn}
            data-testid="idle-stay-loggedin"
            autoFocus
          >
            Tetap Login
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
